import {
  candidateStagesByPerson,
  hiringStages,
  type HiringStage,
  type LifecycleState,
  type OfferRow,
  type OnboardingHiringStage,
  type Person,
  type PersonChecklist,
  personDisplayName,
  type UnreadableOfferRow,
} from '@vigeo/shared';
import { openBlockers, ownerOf } from '@/features/readiness';

/**
 * The Hiring card's five stages (frame 06b `288:215`), built from what the app already knows.
 *
 * NO NEW COUNTER, ANYWHERE. The first two stages are `offerGroups` — read from the very rows
 * `/api/offers` returns, so the dashboard and the Offers list are looking at one answer rather
 * than two that agree by accident. The last three are each onboarding person's own checklist,
 * read through the same query the Person page and the Roster use.
 */

/** Stage copy, verbatim from the frame. `lifecycle` drives the badge the frame draws on each. */
export interface HiringStagePresenter {
  readonly label: string;
  readonly caption: string;
  readonly lifecycle: Extract<LifecycleState, 'candidate' | 'onboarding'>;
}

export const hiringStagePresenters = {
  'prepare-offer': { label: 'Prepare the offer', caption: 'Interviewed, no offer yet', lifecycle: 'candidate' },
  'offer-out': { label: 'Offer out to sign', caption: 'Waiting on their signature', lifecycle: 'candidate' },
  'onboarding-portal': { label: 'Onboarding portal', caption: 'Filling in the application', lifecycle: 'onboarding' },
  'hr-processing': { label: 'HR processing', caption: 'Checks, reviews and records', lifecycle: 'onboarding' },
  orientation: { label: 'Orientation', caption: 'Orientation and competency', lifecycle: 'onboarding' },
} as const satisfies Record<HiringStage, HiringStagePresenter>;

/**
 * How many names a stage shows before "+N more" — the frame's own cap: its three-person stage
 * draws two names and "+1 more".
 */
export const NAMES_SHOWN = 2;

/**
 * Where one onboarding person stands, from their derived checklist.
 *
 * WHOSE MOVE IS IT — that is the whole rule, and it is read through `ownerOf`, the function the
 * Person Overview already uses to answer it, over `openBlockers`, the rows that actually hold up a
 * first assignment. Nothing here re-derives readiness or invents a phase field.
 *
 * The precedence is the frame's own left-to-right order, which is also the order HR can act in:
 *
 * 1. Anything still with the CANDIDATE -> **Onboarding portal**. They are filling something in,
 *    and until they submit there is nothing for HR to process.
 * 2. Otherwise anything on HR's desk -> **HR processing**. Reviews, decisions and the records only
 *    HR can enter.
 * 3. Otherwise -> **Orientation**. Nothing outstanding BLOCKS them, which is exactly the state
 *    someone is in between finishing their paperwork and being activated: orientation, competency
 *    and the final HR clearance `activationIssues` asks for. It is the residue on purpose — an
 *    empty checklist and a finished checklist are the same fact here, and both belong at the end
 *    of the row rather than in a stage that says work is outstanding.
 */
export function onboardingStageOf(checklist: PersonChecklist): OnboardingHiringStage {
  const blockers = openBlockers(checklist);
  if (blockers.some((item) => ownerOf(item) === 'candidate')) return 'onboarding-portal';
  const onHr = new Set(['hr-review', 'hr-decision', 'hr-record']);
  if (blockers.some((item) => onHr.has(ownerOf(item)))) return 'hr-processing';
  return 'orientation';
}

export interface HiringPerson {
  readonly personId: string;
  readonly name: string;
  /** The role label, or '' while the catalog is still loading — never a raw profile key. */
  readonly role: string;
}

export interface HiringStageView {
  readonly stage: HiringStage;
  readonly presenter: HiringStagePresenter;
  /** Everyone at this stage, counted. `people` is the capped head of the same set. */
  readonly count: number;
  readonly people: readonly HiringPerson[];
  /** How many the card does not name. `count - people.length`, so it cannot disagree. */
  readonly more: number;
}

export interface HiringInput {
  /** Every candidate's offer row, from `/api/offers`. Undefined while the request is in flight. */
  readonly offers: readonly OfferRow[] | undefined;
  /**
   * Candidates `/api/offers` could NOT read — its `unavailable` array (#299, #327).
   *
   * Separate from `offers` because that is exactly what they are on the wire: a candidate whose
   * stored document will not parse has no group, no terms and no signature, so there is no
   * `OfferRow` to give them. They are still in the pipeline, which is why they must reach this
   * function at all rather than being left behind with `rows`.
   *
   * Undefined while the request is in flight, like `offers`.
   */
  readonly unreadable: readonly UnreadableOfferRow[] | undefined;
  /** People in the `onboarding` lifecycle. */
  readonly onboarding: readonly Person[];
  /** That person's derived checklist, or null while it is pending or has failed. */
  readonly checklistOf: (personId: string) => PersonChecklist | null;
  readonly roleOf: (keys: readonly string[]) => string;
}

export interface Hiring {
  readonly stages: readonly HiringStageView[];
  /**
   * Everyone the card could not place in a stage, so they are in NONE of the five figures.
   *
   * Reported rather than swallowed. Five confident figures that quietly sum to less than the
   * pipeline is the failure this exists to prevent: an HR admin reads the card as the whole
   * pipeline, and a person missing from it is a person nobody is working.
   *
   * IT NOW HAS TWO SOURCES, and the rule is about the counter rather than about either of them: ANY
   * person the caller holds but cannot place belongs here, never omitted.
   *
   *  1. an onboarding person whose checklist has not arrived;
   *  2. a candidate whose stored document will not parse, which `/api/offers` reports as
   *     `unavailable` rather than as a row (#299) — wired in here by #327, which is what the
   *     previous version of this comment said the honest fix would be.
   *
   * The two are counted separately as well as together, because the card names the REASON and a
   * single total cannot say which applies. See `unplacedReason`.
   */
  readonly unplaced: number;
  /** Of `unplaced`: onboarding people whose checklist has not been derived. */
  readonly unplacedNoChecklist: number;
  /** Of `unplaced`: candidates whose stored record could not be read (#299). */
  readonly unplacedUnreadable: number;
}

/**
 * Why the unplaced people are unplaced, naming ONLY the reasons that actually apply (#327).
 *
 * The card used to say "their checklist has not been derived" unconditionally. Once a degraded
 * candidate counts towards `unplaced` that sentence is FALSE for them — their checklist may well
 * have been derived; nobody knows, because their record could not be read. Making the total honest
 * while leaving the sentence asserting a cause nobody has would just move the lie.
 *
 * So the reason is derived from which sources are actually non-zero. Wording lives here rather than
 * in the component for the reason `offers/model.ts` gives: the sentence should be readable in a test
 * without rendering anything.
 */
export function unplacedReason(hiring: Pick<Hiring, 'unplacedNoChecklist' | 'unplacedUnreadable'>): string {
  const { unplacedNoChecklist, unplacedUnreadable } = hiring;
  if (unplacedNoChecklist > 0 && unplacedUnreadable > 0) {
    return 'some have no derived checklist, and some have a record that could not be read';
  }
  if (unplacedUnreadable > 0) return 'their record could not be read';
  return 'their checklist has not been derived';
}

const byName = (a: HiringPerson, b: HiringPerson) => a.name.localeCompare(b.name);

/**
 * The five stages, in the frame's order, every one of them present — including the empty ones.
 *
 * An empty stage is a fact HR reads ("nothing is waiting on a signature"), and a card whose
 * columns appear and disappear is one nobody can scan. `hiringStages` drives the order, so a sixth
 * stage cannot be added without appearing here.
 */
export function buildHiring(input: HiringInput): Hiring {
  const collected = new Map<HiringStage, HiringPerson[]>(hiringStages.map((stage) => [stage, []]));

  /**
   * ONE ENTRY PER PERSON, from rows that are one per OFFER (#297).
   *
   * `candidateStagesByPerson` folds a candidate's rows to their single stage. Pushing the rows
   * straight in would count a candidate who holds a signed offer and a fresh draft twice, in two
   * different stages — and this card's whole claim is that its five figures plus `unplaced` account
   * for everybody the caller handed it. Anyone it cannot place is `unplaced`'s to report, never
   * dropped; see that field for the second source #317 is adding.
   */
  const stageOf = candidateStagesByPerson(input.offers ?? []);
  const named = new Set<string>();
  for (const row of input.offers ?? []) {
    const stage = stageOf.get(row.personId);
    if (stage === undefined || named.has(row.personId)) continue;
    named.add(row.personId);
    collected.get(stage)?.push({
      personId: row.personId,
      name: row.candidateName,
      role: input.roleOf(row.jobProfileKeys),
    });
  }

  /**
   * A CANDIDATE WHOSE RECORD COULD NOT BE READ IS UNPLACED, not absent (#327).
   *
   * They have no `OfferRow`, so `candidateStagesByPerson` above cannot see them and no stage can
   * hold them — which is precisely the case this counter exists for. Left out, the five figures
   * quietly sum to less than the pipeline and the person is one nobody is working.
   *
   * NOT folded into `prepare-offer` instead: that would assert HR is preparing them an offer, and
   * nobody knows whether they need one. A missing answer, not a wrong one — the same choice
   * `/api/offers` makes by giving them no group at all.
   */
  const unplacedUnreadable = (input.unreadable ?? []).length;

  let unplacedNoChecklist = 0;
  for (const person of input.onboarding) {
    const checklist = input.checklistOf(person.id);
    if (checklist === null) { unplacedNoChecklist += 1; continue; }
    collected.get(onboardingStageOf(checklist))?.push({
      personId: person.id,
      name: personDisplayName(person.name),
      role: input.roleOf(person.jobProfileKeys),
    });
  }

  return {
    // The total is the card's promise: these five figures plus this one account for everybody the
    // caller handed us. It is a sum rather than a third counter so it cannot disagree with its parts.
    unplaced: unplacedNoChecklist + unplacedUnreadable,
    unplacedNoChecklist,
    unplacedUnreadable,
    stages: hiringStages.map((stage) => {
      // Sorted, so two renders of the same data name the same two people rather than whichever
      // two a request happened to answer first.
      const people = [...(collected.get(stage) ?? [])].sort(byName);
      return {
        stage,
        presenter: hiringStagePresenters[stage],
        count: people.length,
        people: people.slice(0, NAMES_SHOWN),
        more: Math.max(0, people.length - NAMES_SHOWN),
      };
    }),
  };
}
